import { useAuthReady } from './useAuthReady';
import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import {
  fetchCard,
  fetchCardIntent,
  fetchCardIntents,
  type CardIntent,
} from '../lib/cards';

const PENDING_POLL_MS = 1500;
/** How long to wait for Banana balance to reflect the debit before toasting anyway. */
const BALANCE_CONFIRM_MS = 25_000;
const BALANCE_POLL_MS = 1_500;
/** USD float slack when comparing pre/post Banana balances. */
const BAL_EPS = 0.02;
/** Suppress extra success toasts/confetti that land within this window. */
const SUCCESS_TOAST_COALESCE_MS = 3_500;

/** Banana has already debit the card — show history + celebrate. */
export function withdrawBananaOk(intent: Pick<CardIntent, 'status' | 'stage'>): boolean {
  if (intent.status === 'success') return true;
  if (intent.status === 'failed') return false;
  return (
    intent.stage === 'banana_ok' ||
    intent.stage === 'payout_queued' ||
    intent.stage === 'payout_failed' ||
    intent.stage === 'settled'
  );
}

/** Still waiting for Banana pull — top plaque "Выводим…". */
export function withdrawInFlight(intent: Pick<CardIntent, 'status' | 'stage'>): boolean {
  if (intent.status === 'pending') return true;
  if (intent.status === 'processing' && !withdrawBananaOk(intent)) return true;
  return false;
}

export type WithdrawToast = {
  kind: 'success' | 'error';
  message: string;
  amount?: number;
};

type PlaqueHoldMap = Record<string, CardIntent>;

/** In-memory only — never put this in React Query (dehydrate → localStorage). */
let plaqueHolds: PlaqueHoldMap = {};
const plaqueListeners = new Set<() => void>();

function subscribePlaqueHolds(cb: () => void) {
  plaqueListeners.add(cb);
  return () => {
    plaqueListeners.delete(cb);
  };
}

function getPlaqueHolds() {
  return plaqueHolds;
}

function setPlaqueHold(cardId: string, intent: CardIntent | null) {
  const prev = plaqueHolds[cardId];
  if (intent) {
    if (prev === intent) return;
    plaqueHolds = { ...plaqueHolds, [cardId]: intent };
  } else {
    if (!prev) return;
    const next = { ...plaqueHolds };
    delete next[cardId];
    plaqueHolds = next;
  }
  plaqueListeners.forEach((l) => l());
}

function sleep(ms: number) {
  return new Promise<void>((r) => {
    window.setTimeout(r, ms);
  });
}

function cachedCardBalance(
  qc: QueryClient,
  cardId: string,
  userId: string | number | null | undefined,
): number | null {
  const row = qc.getQueryData<{ balance?: { balance?: number } | null }>([
    'card',
    cardId,
    'balance',
    userId,
  ]);
  const n = row?.balance?.balance;
  return n != null && Number.isFinite(n) ? n : null;
}

/** Poll until live balance drops by ~amount (or any clear drop), or deadline. */
async function waitForBalanceDrop(opts: {
  qc: QueryClient;
  initData: string;
  cardId: string;
  userId: string | number | null | undefined;
  amount: number;
  before: number | null;
}): Promise<void> {
  const { qc, initData, cardId, userId, amount, before } = opts;
  const key = ['card', cardId, 'balance', userId] as const;
  const deadline = Date.now() + BALANCE_CONFIRM_MS;
  const target =
    before != null && amount > 0 ? Math.max(0, before - amount + BAL_EPS) : null;

  while (Date.now() < deadline) {
    try {
      const live = await qc.fetchQuery({
        queryKey: key,
        queryFn: () => fetchCard(initData, cardId),
      });
      const bal = live?.balance?.balance;
      if (bal != null && Number.isFinite(bal)) {
        if (target != null && bal <= target) return;
        if (before != null && bal < before - BAL_EPS) return;
      }
    } catch {
      /* keep waiting */
    }
    await sleep(BALANCE_POLL_MS);
  }
}

/** Map of cardId → withdraw still covering the face (Banana in flight, or
 *  banana_ok while we wait for the live balance to drop). */
export function usePendingWithdrawals(): Record<string, CardIntent> {
  const { canFetch: ready, initData, cacheUserId: userId } = useAuthReady();
  const q = useQuery({
    queryKey: ['card-intents', 'withdraw', 'open', userId],
    enabled: ready,
    queryFn: () => fetchCardIntents(initData!, { kind: 'withdraw', status: 'open' }),
    refetchOnWindowFocus: true,
    refetchOnMount: 'always',
    refetchInterval: (query) => ((query.state.data?.intents?.length ?? 0) > 0 ? PENDING_POLL_MS : false),
  });
  const holds = useSyncExternalStore(subscribePlaqueHolds, getPlaqueHolds, getPlaqueHolds);

  const map: Record<string, CardIntent> = { ...holds };
  for (const intent of q.data?.intents ?? []) {
    if (!intent.cardId || !withdrawInFlight(intent)) continue;
    if (!(intent.cardId in map)) map[intent.cardId] = intent;
  }
  return map;
}

/**
 * After Banana pulls the card: wait for face balance to drop, then toast.
 * Mount once near the wallet root so closing WithdrawSheet does not cancel it.
 */
export function useWithdrawSettlements(): {
  toast: WithdrawToast | null;
  dismiss: () => void;
} {
  const { t } = useTranslation();
  const { canFetch: ready, initData, cacheUserId: userId } = useAuthReady();
  const qc = useQueryClient();
  const [toast, setToast] = useState<WithdrawToast | null>(null);

  const q = useQuery({
    queryKey: ['card-intents', 'withdraw', 'open', userId],
    enabled: ready,
    queryFn: () => fetchCardIntents(initData!, { kind: 'withdraw', status: 'open' }),
    refetchOnWindowFocus: true,
    refetchOnMount: 'always',
    refetchInterval: (query) => ((query.state.data?.intents?.length ?? 0) > 0 ? PENDING_POLL_MS : false),
  });

  const prevOpenRef = useRef<Map<string, CardIntent>>(new Map());
  const seenRef = useRef(false);
  const celebratedRef = useRef<Set<string>>(new Set());
  const handlingRef = useRef<Set<string>>(new Set());
  /** Intents that left the open list / failed a fetch — retry until settled. */
  const retryRef = useRef<Map<string, CardIntent>>(new Map());
  const lastSuccessToastAtRef = useRef(0);
  const toastTimerRef = useRef<number | null>(null);

  const showToast = (next: WithdrawToast) => {
    if (next.kind === 'success') {
      const now = Date.now();
      if (now - lastSuccessToastAtRef.current < SUCCESS_TOAST_COALESCE_MS) {
        return; // already celebrating — don't stack confetti for sibling cards
      }
      lastSuccessToastAtRef.current = now;
    }
    if (toastTimerRef.current != null) window.clearTimeout(toastTimerRef.current);
    setToast(next);
    toastTimerRef.current = window.setTimeout(
      () => setToast(null),
      next.kind === 'success' ? 2800 : 3200,
    );
  };

  useEffect(() => {
    if (!ready) return;
    const intents = q.data?.intents ?? [];
    const openMap = new Map(intents.map((i) => [i.id, i]));

    if (!seenRef.current) {
      if (q.isFetched) {
        prevOpenRef.current = openMap;
        // Anything already past Banana must not fire a welcome-back confetti burst.
        for (const [id, intent] of openMap) {
          if (!withdrawInFlight(intent)) celebratedRef.current.add(id);
        }
        seenRef.current = true;
      }
      return;
    }

    const prev = prevOpenRef.current;
    const candidates = new Set<string>([
      ...prev.keys(),
      ...openMap.keys(),
      ...retryRef.current.keys(),
    ]);

    for (const id of candidates) {
      if (celebratedRef.current.has(id) || handlingRef.current.has(id)) continue;
      const before = prev.get(id) ?? retryRef.current.get(id);
      const now = openMap.get(id);

      // Still waiting on Banana.
      if (now && withdrawInFlight(now)) {
        retryRef.current.delete(id);
        continue;
      }

      // Only celebrate a real transition out of "Выводим…". Already-open
      // banana_ok rows (and remount/poll echoes) are ignored — unless this is
      // a fetch retry after we already saw the transition.
      const fromRetry = retryRef.current.has(id);
      const leftInFlight = !!before && withdrawInFlight(before);
      if (!leftInFlight && !fromRetry) {
        if (now && withdrawBananaOk(now)) celebratedRef.current.add(id);
        continue;
      }

      const maybeDone = now ?? before;
      if (!maybeDone) continue;

      if (now?.cardId && withdrawBananaOk(now)) {
        setPlaqueHold(now.cardId, now);
      }

      handlingRef.current.add(id);
      void (async () => {
        try {
          const { intent } = await fetchCardIntent(initData, id);
          const amount = intent.amount != null ? Number(intent.amount) : 0;

          if (withdrawBananaOk(intent)) {
            if (intent.cardId) {
              setPlaqueHold(intent.cardId, intent);
              const prior = cachedCardBalance(qc, intent.cardId, userId);
              await waitForBalanceDrop({
                qc,
                initData,
                cardId: intent.cardId,
                userId,
                amount: Number.isFinite(amount) ? amount : 0,
                before: prior,
              });
              await Promise.all([
                qc.invalidateQueries({ queryKey: ['card', intent.cardId, 'transactions'] }),
                qc.invalidateQueries({ queryKey: ['cards'] }),
              ]);
              setPlaqueHold(intent.cardId, null);
            }
            retryRef.current.delete(id);
            celebratedRef.current.add(id);
            showToast({
              kind: 'success',
              message: t('cards.withdrawDone'),
              amount: Number.isFinite(amount) && amount > 0 ? amount : undefined,
            });
          } else if (intent.status === 'failed') {
            if (intent.cardId) setPlaqueHold(intent.cardId, null);
            retryRef.current.delete(id);
            celebratedRef.current.add(id);
            if (intent.cardId) {
              await qc.invalidateQueries({ queryKey: ['card', intent.cardId, 'balance'] });
            }
            showToast({
              kind: 'error',
              message: t('cards.withdrawFailed'),
              amount: Number.isFinite(amount) && amount > 0 ? amount : undefined,
            });
          } else {
            if (intent.cardId) setPlaqueHold(intent.cardId, null);
            retryRef.current.delete(id);
          }
        } catch {
          if (maybeDone.cardId) setPlaqueHold(maybeDone.cardId, null);
          retryRef.current.set(id, maybeDone);
        } finally {
          handlingRef.current.delete(id);
        }
      })();
    }

    prevOpenRef.current = openMap;
  }, [q.data, q.isFetched, ready, initData, qc, t, userId]);

  return {
    toast,
    dismiss: () => {
      if (toastTimerRef.current != null) window.clearTimeout(toastTimerRef.current);
      setToast(null);
    },
  };
}
